import { MetricChangeIndicator } from "@/components/application/metrics/metrics";
import { TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import type { PeriodSummary, QualityBand } from "../flag-qa-types";
import { QUALITY_BAND_COLORS } from "../flag-qa-types";
import { calculateDelta, formatTimeToAction } from "../flag-qa-utils";

const QUALITY_BAND_LABELS: Record<QualityBand, string> = {
  low: "Low",
  moderate: "Moderate",
  high: "High",
};

const QUALITY_BAND_BADGE_COLORS: Record<QualityBand, "error" | "warning" | "success"> = {
  low: "error",
  moderate: "warning",
  high: "success",
};

interface FlagQaComparisonKpisProps {
  periodA: {
    summary: PeriodSummary;
    label: string;
    dateRange: string;
  };
  periodB: {
    summary: PeriodSummary;
    label: string;
    dateRange: string;
  };
}

interface ComparisonCardProps {
  title: string;
  periodAValue: number | string;
  periodBValue: number | string;
  format?: "number" | "percent" | "time";
  higherIsBetter?: boolean;
  periodALabel: string;
  periodBLabel: string;
  periodASubtext?: string;
  periodBSubtext?: string;
}

function ComparisonCard({
  title,
  periodAValue,
  periodBValue,
  format = "number",
  higherIsBetter = true,
  periodALabel,
  periodBLabel,
  periodASubtext,
  periodBSubtext,
}: ComparisonCardProps) {
  const numA = typeof periodAValue === "number" ? periodAValue : 0;
  const numB = typeof periodBValue === "number" ? periodBValue : 0;

  const delta = calculateDelta(numA, numB);
  // For metrics where lower is better (like time to action), flip the trend
  const displayTrend = higherIsBetter ? delta.trend : delta.trend === "positive" ? "negative" : "positive";

  const formatValue = (value: number | string) => {
    if (typeof value === "string") return value;
    if (format === "percent") return `${value.toFixed(1)}%`;
    if (format === "time") return formatTimeToAction(value);
    return value.toLocaleString();
  };

  return (
    <TableCard.Root className="p-4">
      <div className="text-sm text-tertiary mb-3">{title}</div>
      <div className="flex justify-between items-end">
        <div className="flex-1">
          <div className="text-xs text-tertiary mb-1">{periodALabel}</div>
          <div className="text-2xl font-bold text-primary">{formatValue(periodAValue)}</div>
          {periodASubtext && <div className="text-xs text-tertiary mt-1">{periodASubtext}</div>}
        </div>
        <div className="flex flex-col items-center px-4">
          <MetricChangeIndicator type="trend" trend={displayTrend} value={`${delta.value.toFixed(1)}%`} />
        </div>
        <div className="flex-1 text-right">
          <div className="text-xs text-tertiary mb-1">{periodBLabel}</div>
          <div className="text-2xl font-bold text-secondary">{formatValue(periodBValue)}</div>
          {periodBSubtext && <div className="text-xs text-tertiary mt-1">{periodBSubtext}</div>}
        </div>
      </div>
    </TableCard.Root>
  );
}

export function FlagQaComparisonKpis({ periodA, periodB }: FlagQaComparisonKpisProps) {
  return (
    <div className="flex flex-col gap-4">
      {/* Period labels header */}
      <div className="flex justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-primary">{periodA.label}</span>
          <span className="text-tertiary">{periodA.dateRange}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-secondary">{periodB.label}</span>
          <span className="text-tertiary">{periodB.dateRange}</span>
        </div>
      </div>

      {/* Row 1: Flag counts */}
      <div className="grid grid-cols-3 gap-4">
        <ComparisonCard
          title="Total Flags"
          periodAValue={periodA.summary.totalFlags}
          periodBValue={periodB.summary.totalFlags}
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
        />
        <ComparisonCard
          title="Reviewed Flags"
          periodAValue={periodA.summary.reviewedFlags}
          periodBValue={periodB.summary.reviewedFlags}
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
          periodASubtext={`${periodA.summary.totalFlags > 0 ? ((periodA.summary.reviewedFlags / periodA.summary.totalFlags) * 100).toFixed(1) : 0}%`}
          periodBSubtext={`${periodB.summary.totalFlags > 0 ? ((periodB.summary.reviewedFlags / periodB.summary.totalFlags) * 100).toFixed(1) : 0}%`}
        />
        {periodA.summary.avgTimeToActionHours != null && periodB.summary.avgTimeToActionHours != null && (
          <ComparisonCard
            title="Avg. Time to Action"
            periodAValue={periodA.summary.avgTimeToActionHours}
            periodBValue={periodB.summary.avgTimeToActionHours}
            format="time"
            higherIsBetter={false}
            periodALabel={periodA.label}
            periodBLabel={periodB.label}
          />
        )}
      </div>

      {/* Row 2: Quality metrics */}
      <div className="grid grid-cols-4 gap-4">
        <ComparisonCard
          title="Recall"
          periodAValue={periodA.summary.recall}
          periodBValue={periodB.summary.recall}
          format="percent"
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
        />
        <ComparisonCard
          title="Precision"
          periodAValue={periodA.summary.precision}
          periodBValue={periodB.summary.precision}
          format="percent"
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
        />
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-3">F1 Score</div>
          <div className="flex justify-between items-end">
            <div className="flex-1">
              <div className="text-xs text-tertiary mb-1">{periodA.label}</div>
              <div className="text-2xl font-bold" style={{ color: QUALITY_BAND_COLORS[periodA.summary.qualityBand] }}>
                {periodA.summary.f1.toFixed(1)}%
              </div>
              <Badge color={QUALITY_BAND_BADGE_COLORS[periodA.summary.qualityBand]} size="sm" className="mt-1">
                {QUALITY_BAND_LABELS[periodA.summary.qualityBand]}
              </Badge>
            </div>
            <div className="flex flex-col items-center px-4">
              <MetricChangeIndicator
                type="trend"
                trend={calculateDelta(periodA.summary.f1, periodB.summary.f1).trend}
                value={`${calculateDelta(periodA.summary.f1, periodB.summary.f1).value.toFixed(1)}%`}
              />
            </div>
            <div className="flex-1 text-right">
              <div className="text-xs text-tertiary mb-1">{periodB.label}</div>
              <div className="text-2xl font-bold" style={{ color: QUALITY_BAND_COLORS[periodB.summary.qualityBand] }}>
                {periodB.summary.f1.toFixed(1)}%
              </div>
              <div className="flex justify-end">
                <Badge color={QUALITY_BAND_BADGE_COLORS[periodB.summary.qualityBand]} size="sm" className="mt-1">
                  {QUALITY_BAND_LABELS[periodB.summary.qualityBand]}
                </Badge>
              </div>
            </div>
          </div>
        </TableCard.Root>
        <ComparisonCard
          title="Accuracy"
          periodAValue={periodA.summary.accuracy}
          periodBValue={periodB.summary.accuracy}
          format="percent"
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
        />
      </div>

      {/* Row 3: Classification breakdown */}
      <div className="grid grid-cols-3 gap-4">
        <ComparisonCard
          title="False Positives (FP)"
          periodAValue={periodA.summary.fp}
          periodBValue={periodB.summary.fp}
          higherIsBetter={false}
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
        />
        <ComparisonCard
          title="True Positives (TP)"
          periodAValue={periodA.summary.tp}
          periodBValue={periodB.summary.tp}
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
        />
        <ComparisonCard
          title="False Negatives (FN)"
          periodAValue={periodA.summary.fn}
          periodBValue={periodB.summary.fn}
          higherIsBetter={false}
          periodALabel={periodA.label}
          periodBLabel={periodB.label}
        />
      </div>
    </div>
  );
}
